// Central FAQ SEED content for /faq page and /admin/faq editor.
// This module is the FALLBACK / initial-seed. Live content is served from
// GET /api/faq (admin-editable). Answers are markdown strings so they can
// be stored in the database and edited from the admin console.
//
// EVERY answer here has been audited against the actual codebase — no
// invented features, button labels, or menu paths. If a specific flow
// isn't verified, the answer stays general rather than describing a UI
// that doesn't exist.

export const FAQ_CATEGORIES = [
  { id: 'getting-started', label: 'Getting started', icon: 'Sparkles' },
  { id: 'chat', label: 'Chatting with Eili', icon: 'MessageCircle' },
  { id: 'uploads', label: 'Files & analysis', icon: 'Paperclip' },
  { id: 'bible', label: 'Bible reader', icon: 'BookOpen' },
  { id: 'community', label: 'Community', icon: 'Users' },
  { id: 'sharing', label: 'Sharing & saving', icon: 'Share2' },
  { id: 'subscription', label: 'Subscription & billing', icon: 'CreditCard' },
  { id: 'account', label: 'Account & settings', icon: 'Settings' },
  { id: 'troubleshooting', label: 'Troubleshooting', icon: 'LifeBuoy' },
];

/* eslint-disable no-multi-str */
export const FAQ_ITEMS = [
  // ─── Getting started ─────────────────────────────────────────────
  {
    id: 'what-is-eili',
    category: 'getting-started',
    question: 'What is Eili?',
    tags: ['about', 'intro', 'app'],
    answer: `Eili is a biblical research companion. Ask any question about Scripture and Eili answers with careful, sourced explanations grounded in the Bible — not opinions. It draws from public-domain Bible translations, classical commentators, and Greek/Hebrew word studies, and stays inside its scope: the Bible and biblical theology.`,
    screenshots: [
      { src: '/faq/mobile-home.png', alt: 'Eili home screen on mobile', viewport: 'mobile' },
      { src: '/faq/desktop-home.png', alt: 'Eili home screen on desktop', viewport: 'desktop' },
    ],
  },
  {
    id: 'sign-up',
    category: 'getting-started',
    question: 'How do I sign up or sign in?',
    tags: ['account', 'register', 'login'],
    answer: `From the sign-in screen, tap **Sign in with Email**, enter an email + password, and you're in. If Google sign-in is available for you, use that instead. Forgot your password? Use the *Forgot password* link on the sign-in screen.`,
    screenshots: [],
  },
  {
    id: 'free-tier',
    category: 'getting-started',
    question: 'What does the free tier include?',
    tags: ['pricing', 'free', 'plan'],
    answer: `Free-tier users get daily chats with Eili, the Bible reader, daily reflections, and community access. Study Tools (Deep Study, Historical Context, Greek Analysis, Gospel Lens, Easy to Read), file uploads, YouTube analysis, and Weekly Recaps are on paid tiers. Open your Settings → Account to see your current plan.`,
    screenshots: [],
  },
  {
    id: 'scripture-accuracy',
    category: 'getting-started',
    question: 'How does Eili ensure Scripture accuracy?',
    tags: ['accuracy', 'trust', 'scripture', 'hallucination', 'verses'],
    answer: `Every verse Eili quotes is pulled directly from the actual translation you've chosen (ESV, NIV, LSB, KJV, and 8+ others) — not generated or paraphrased on the fly. Verse quotations are checked against the real translation text before you see them. Commentary and historical context are drawn from a vetted reference library rather than composed from scratch. The response is built *around* those verified sources, so the Scripture you read is the Scripture that was actually written.`,
    screenshots: [],
  },
  {
    id: 'denominational-stance',
    category: 'getting-started',
    question: 'Does Eili represent a specific denomination?',
    tags: ['denomination', 'theology', 'bias', 'reformed', 'catholic', 'protestant'],
    answer: `Eili does not push a specific denomination. When a passage is interpreted differently across traditions (e.g., baptism, communion, church governance, end-times), Eili aims to present the main views honestly and label them — using phrases like "widely held view", "majority view", or "debated" — rather than picking a side. You'll see attributed positions (Reformed, Catholic, Anglican, Orthodox, etc.) where relevant, so you can weigh the answers against your own church and Scripture itself.`,
    screenshots: [],
  },
  {
    id: 'sermon-prep',
    category: 'getting-started',
    question: 'Can I use Eili for sermon prep?',
    tags: ['sermon', 'pastor', 'teacher', 'deep-study', 'lesson'],
    answer: `Yes. Premium unlocks Deep Study Mode, which pulls together commentary, cross-references, Greek/Hebrew word studies, and historical context on any passage in one shot — designed specifically for pastors and lay teachers preparing a sermon or lesson. You can save the response, export it, or keep the conversation open and refine it with follow-up questions.`,
    screenshots: [],
  },
  {
    id: 'data-to-providers',
    category: 'getting-started',
    question: 'Is my data sent to third-party providers?',
    tags: ['privacy', 'data', 'providers', 'openai', 'gemini'],
    answer: `To generate responses, your chat messages and the biblical passages you ask about are sent to third-party model providers (currently Google Gemini and OpenAI). Only the content of your questions is sent — not your name, email, device info, location, or contacts. Providers are contractually bound not to use your data for model training. You can delete your conversations at any time from Settings. Full details are in our [Privacy Policy](/privacy).`,
    screenshots: [],
  },
  {
    id: 'report-error',
    category: 'getting-started',
    question: "What if I find an error in a response?",
    tags: ['error', 'feedback', 'accuracy', 'correction'],
    answer: `Please tell us. Every response has a thumbs-up/thumbs-down. Tapping thumbs-down opens a short form where you can describe what was wrong (wrong verse, misattributed quote, weak reasoning). Those reports go straight to us and we use them to improve the guardrails. Treat Eili as a study companion, not a final authority — always weigh responses against Scripture itself.`,
    screenshots: [],
  },

  // ─── Chatting with Eili ─────────────────────────────────────────
  {
    id: 'ask-question',
    category: 'chat',
    question: 'How do I ask Eili a question?',
    tags: ['chat', 'ask', 'question'],
    answer: `Type your question in the chat input at the bottom of the screen and send. Eili streams its answer as it thinks. If you're not sure where to start, tap any of the *Try asking* suggestions on a fresh conversation.`,
    screenshots: [
      { src: '/faq/mobile-chat.png', alt: 'Chat screen with suggested prompts', viewport: 'mobile' },
    ],
  },
  {
    id: 'study-tools',
    category: 'chat',
    question: 'What are Study Tools?',
    tags: ['study', 'tools', 'greek', 'commentary', 'deep', 'gospel', 'lens'],
    answer: `Study Tools are togglable lenses that reshape Eili's response for the way you're studying:

- **Deep Study** — multi-layered analysis covering theology, context, and application.
- **Historical Context** — scholarly background on the historical setting, cultural practices, and authorial context.
- **Greek Analysis** — original-language word study, transliteration, and lexical commentary.
- **Gospel Lens** — a Christ-centered reading that highlights redemptive themes and hope.
- **Easy to Read** — plain-language responses that stay simple and clear.

Open the **Tools** panel above the chat input to toggle any of them on for your next message. Most tools are paid-tier features.`,
    screenshots: [
      { src: '/faq/mobile-study-tools.png', alt: 'Study Tools popover open', viewport: 'mobile' },
    ],
  },
  {
    id: 'reopen-conversation',
    category: 'chat',
    question: 'How do I go back to an old conversation?',
    tags: ['history', 'conversation', 'sidebar', 'reopen'],
    answer: `On mobile, tap the menu icon in the top-left of the chat screen to open your conversation list. On desktop the sidebar is visible by default. Every chat is auto-saved — tap any row to reopen it.`,
    screenshots: [
      { src: '/faq/mobile-sidebar.png', alt: 'Conversation sidebar open on mobile', viewport: 'mobile' },
    ],
  },
  {
    id: 'rename-conversation',
    category: 'chat',
    question: 'Can I rename, pin, or delete a conversation?',
    tags: ['rename', 'delete', 'pin', 'organize'],
    answer: `Yes, from the conversation sidebar:

- **Rename** — double-tap a conversation's title, type the new name, hit Enter.
- **Pin** — tap the pin icon on a row to keep it at the top of your list.
- **Delete** — tap the trash icon on a row and confirm. This can't be undone.`,
    screenshots: [],
  },
  {
    id: 'context-memory',
    category: 'chat',
    question: 'Does Eili remember earlier messages in a conversation?',
    tags: ['context', 'memory', 'follow-up'],
    answer: `Yes — within a single conversation, Eili keeps the recent history in mind, so you can ask follow-ups like "expand on the second point" or "what does the Greek say here" without repeating context. Start a new conversation when you want a fresh, uninfluenced answer.`,
    screenshots: [
      { src: '/faq/mobile-chat-response.png', alt: 'Chat response', viewport: 'mobile' },
    ],
  },

  // ─── Files & analysis ───────────────────────────────────────────
  {
    id: 'file-types',
    category: 'uploads',
    question: 'What file types can Eili analyze?',
    tags: ['upload', 'pdf', 'docx', 'txt', 'files'],
    answer: `Eili reads three formats:

- **.pdf** — text-based PDFs (sermon notes, articles, study guides)
- **.docx** — Word documents, including tables
- **.txt** — plain text of any common encoding

Legacy \`.doc\` files aren't supported directly — open in Word or Google Docs and export as .docx or PDF first. Scanned image-only PDFs won't work either; Eili can only read text that's actually embedded in the file.`,
    screenshots: [],
  },
  {
    id: 'upload-file',
    category: 'uploads',
    question: 'How do I upload a file to a chat?',
    tags: ['upload', 'attach', 'how-to'],
    answer: `Inside any chat, open the **Tools** panel above the chat input and tap **Upload File**. Pick a .pdf, .docx, or .txt file. Eili acknowledges it with a small paperclip chip so you always see what's in context, then you can ask questions grounded in the document ("Summarize section 3", "What does the author say about grace?"). File upload is a paid-tier feature.`,
    screenshots: [
      { src: '/faq/mobile-study-tools.png', alt: 'Upload File option in Tools panel', viewport: 'mobile' },
    ],
  },
  {
    id: 'password-protected-pdf',
    category: 'uploads',
    question: 'Why does Eili say my PDF is password-protected?',
    tags: ['pdf', 'encrypted', 'error'],
    answer: `Password-protected (encrypted) PDFs can't be read without the password. Open the PDF in Preview / Adobe Reader, enter the password, then export a fresh copy without a password and upload the new file.

If Eili says *"couldn't read any text"* instead, the PDF is likely a scanned image. Export a text-based PDF or copy the content into a .docx and upload that.`,
    screenshots: [],
  },
  {
    id: 'file-persistence',
    category: 'uploads',
    question: 'Does the file stay in the conversation?',
    tags: ['storage', 'privacy', 'file'],
    answer: `Yes — the extracted text is stored securely against that conversation only, so any follow-up question in the same chat can reference it. Delete the conversation and the file's text is deleted with it. Files aren't shared into other chats.`,
    screenshots: [],
  },
  {
    id: 'youtube-analysis',
    category: 'uploads',
    question: 'Can Eili analyze YouTube sermons and videos?',
    tags: ['youtube', 'sermon', 'video'],
    answer: `Yes. Open the **Tools** panel and choose **YouTube Analysis**, then paste a video URL. Eili pulls the transcript and you can ask for a breakdown, cited passages, or a critical review. Paid-tier feature.`,
    screenshots: [
      { src: '/faq/mobile-study-tools.png', alt: 'YouTube Analysis option in Tools panel', viewport: 'mobile' },
    ],
  },

  // ─── Bible reader ───────────────────────────────────────────────
  {
    id: 'open-bible',
    category: 'bible',
    question: 'How do I open the Bible reader?',
    tags: ['bible', 'reader', 'open'],
    answer: `Tap the **Bible** icon in the top navigation on any page to open the Bible reader.`,
    screenshots: [
      { src: '/faq/mobile-bible.png', alt: 'Bible reader on mobile', viewport: 'mobile' },
      { src: '/faq/desktop-bible.png', alt: 'Bible reader on desktop', viewport: 'desktop' },
    ],
  },
  {
    id: 'switch-translations',
    category: 'bible',
    question: 'How do I switch translations?',
    tags: ['translation', 'kjv', 'niv', 'esv'],
    answer: `Inside the Bible reader, use the translation selector to switch versions. Available translations depend on your tier; premium translations are unlocked on paid plans.`,
    screenshots: [],
  },
  {
    id: 'reading-plans',
    category: 'bible',
    question: 'Do you have reading plans like "Bible in a Year"?',
    tags: ['reading-plan', 'bible-in-a-year', 'daily'],
    answer: `Yes — a *Bible in a Year* reading plan is included. Follow the daily reading and ask Eili about anything in that day's passages right from the same conversation.`,
    screenshots: [],
  },

  // ─── Community ──────────────────────────────────────────────────
  {
    id: 'what-is-community',
    category: 'community',
    question: 'What is the Community section?',
    tags: ['community', 'social', 'people'],
    answer: `Community is a shared space to post reflections, discuss questions, and connect with other Eili users. Your private conversations, Vault chats, and bookmarks never appear in Community.`,
    screenshots: [
      { src: '/faq/mobile-community.png', alt: 'Community feed on mobile', viewport: 'mobile' },
    ],
  },
  {
    id: 'friend-someone',
    category: 'community',
    question: 'How do I add a friend?',
    tags: ['friend', 'follow', 'invite'],
    answer: `Head to Community and send a friend request from another user's profile. Once they accept, you'll see each other in the friend list and can share content directly (verses, reflections, recap cards).`,
    screenshots: [],
  },
  {
    id: 'stay-private',
    category: 'community',
    question: 'Can I keep my studies private?',
    tags: ['privacy', 'private', 'vault'],
    answer: `Every chat is private to your account by default — nothing you type in chat appears in Community unless you explicitly share it. For extra protection, use the **Vault**: move sensitive conversations there and they're locked behind a PIN (or biometric on supported devices), and excluded from sharing and Weekly Recaps.`,
    screenshots: [],
  },

  // ─── Sharing & saving ───────────────────────────────────────────
  {
    id: 'weekly-recap',
    category: 'sharing',
    question: 'What are Weekly Recaps?',
    tags: ['recap', 'weekly', 'summary'],
    answer: `Each week Eili can summarise the topics you studied and the verses you engaged with into a shareable Recap card. Open **Recap** from the top navigation to view yours. Weekly Recaps are a paid-tier feature.`,
    screenshots: [],
  },
  {
    id: 'share-with-friend',
    category: 'sharing',
    question: 'How do I share a verse or reflection with a friend?',
    tags: ['share', 'social', 'export'],
    answer: `Verses and shareable items include a share button that generates a formatted card you can send anywhere — SMS, WhatsApp, email — and the recipient sees a proper preview even if they don't use Eili. In-app sharing to a friend requires you to add them from Community first.`,
    screenshots: [],
  },
  {
    id: 'saved-chats',
    category: 'sharing',
    question: 'How do I save messages to look at later?',
    tags: ['save', 'bookmark', 'vault'],
    answer: `Tap the bookmark icon on any message to save it. Bookmarks live in the sidebar's **Bookmarks** tab, and you can organise them into groups.`,
    screenshots: [
      { src: '/faq/mobile-sidebar.png', alt: 'Bookmarks tab in the sidebar', viewport: 'mobile' },
    ],
  },

  // ─── Subscription & billing ────────────────────────────────────
  {
    id: 'plan-differences',
    category: 'subscription',
    question: 'What do paid tiers unlock?',
    tags: ['pricing', 'plans', 'plus', 'pro'],
    answer: `Paid tiers unlock Study Tools (Deep Study, Historical Context, Greek Analysis, Gospel Lens, Easy to Read), file uploads, YouTube analysis, Weekly Recaps, premium translations, and higher daily chat limits. See the in-app upgrade screen for exact plan pricing and limits.`,
    screenshots: [],
  },
  {
    id: 'upgrade-cancel',
    category: 'subscription',
    question: 'How do I upgrade, downgrade, or cancel?',
    tags: ['billing', 'upgrade', 'cancel', 'refund'],
    answer: `On the **web**, manage your subscription from **Settings → Account**. On iOS and Android native apps, subscriptions are managed through Apple ID or Google Play settings (that's a store requirement, not something we can override). Cancellations always keep your paid features active until the end of the current billing cycle.`,
    screenshots: [
      { src: '/faq/desktop-settings.png', alt: 'Settings page', viewport: 'desktop' },
    ],
  },

  // ─── Account & settings ─────────────────────────────────────────
  {
    id: 'change-profile',
    category: 'account',
    question: 'How do I change my display name, photo, or password?',
    tags: ['profile', 'avatar', 'password', 'account'],
    answer: `Open **Settings → Account**. You can update your display name, upload a new photo, and change your password from there.`,
    screenshots: [
      { src: '/faq/mobile-settings.png', alt: 'Settings screen on mobile', viewport: 'mobile' },
    ],
  },
  {
    id: 'notifications',
    category: 'account',
    question: 'How do I turn on push notifications?',
    tags: ['push', 'notifications'],
    answer: `Notification preferences live inside **Settings → Account**. Toggle *Push notifications* on and accept your browser or phone's permission prompt. iOS Safari users need to install Eili to the home screen first (Safari → Share → *Add to Home Screen*) before iOS allows push.`,
    screenshots: [],
  },
  {
    id: 'change-language',
    category: 'account',
    question: 'How do I switch languages?',
    tags: ['language', 'i18n', 'spanish', 'portuguese', 'french'],
    answer: `Open **Settings → Preferences** and pick your language (English, Español, Português, Français). The UI switches immediately. Eili also detects the language you write in and replies in kind, so you can chat in Spanish while keeping menus in English if you prefer.`,
    screenshots: [],
  },
  {
    id: 'delete-account',
    category: 'account',
    question: 'How do I download my data or delete my account?',
    tags: ['gdpr', 'export', 'delete', 'data'],
    answer: `Both live inside **Settings → Account**. Export produces a copy of your chats, bookmarks, and reflections. Delete permanently removes your data — email us at support@askeili.com if you need help.`,
    screenshots: [],
  },

  // ─── Troubleshooting ────────────────────────────────────────────
  {
    id: 'android-file-attach',
    category: 'troubleshooting',
    question: "The Android app won't let me attach files",
    tags: ['android', 'upload', 'file-picker'],
    answer: `Try these in order:

1. Make sure the Eili Android app is up to date (Play Store → Manage apps → Updates).
2. Force-stop the app from Android's app info screen and reopen it.
3. If the picker opens but shows no files, tap the hamburger in its top-left corner and switch to *Files* / *Recent*. Some Android builds default to an empty source.
4. If the picker still won't open, use the web version at [askeili.com](https://askeili.com) while we work on the native fix.`,
    screenshots: [],
  },
  {
    id: 'logged-out',
    category: 'troubleshooting',
    question: 'I keep getting signed out',
    tags: ['login', 'session', 'sign-out'],
    answer: `If you're being signed out often, your browser or device is likely clearing cookies on close. Whitelist \`askeili.com\` in your browser's cookie settings so your session persists across sessions.`,
    screenshots: [],
  },
  {
    id: 'app-stuck-old',
    category: 'troubleshooting',
    question: 'The app looks stuck on an old version',
    tags: ['cache', 'stale', 'refresh', 'update'],
    answer: `Your browser or PWA cached the previous JS bundle. Fix it with:

1. Pull-down to refresh on mobile, or hard-refresh (Ctrl/Cmd + Shift + R) on desktop.
2. If that doesn't work, clear cached files just for \`askeili.com\` from your browser settings.`,
    screenshots: [],
  },
  {
    id: 'no-push',
    category: 'troubleshooting',
    question: "Push notifications aren't arriving",
    tags: ['push', 'notifications', 'not-working'],
    answer: `Walk through these:

1. **Settings → Account** — is the push toggle on? Try toggling it off and back on.
2. Check your phone's system notification settings for Eili (and for your browser if you're on the web).
3. On iOS Safari, notifications only work if Eili is installed to your home screen (Share → *Add to Home Screen*).
4. Do Not Disturb and Focus modes silently mute push notifications.`,
    screenshots: [],
  },
];
